import { useCallback, useState } from "react";
import { validateBlueprint } from "../../LeadFields/tableBlueprint";
import { importPipelineRows, loadPipelineGoogleSheet, updatePipelineRows } from "./pipelineImportService";
import {
  parsePipelineFile,
  parsePastedPipelineRows,
  suggestPipelineMapping,
} from "./pipelineImportUtils";

const EMPTY_DATA = { columns: [], rows: [], totalRows: 0, truncated: false };

const readError = (error, fallback) =>
  error?.response?.data?.message ||
  Object.values(error?.response?.data?.errors || {})[0]?.[0] ||
  error?.message ||
  fallback;

export default function usePipelineImport({ pipelineId = null } = {}) {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(EMPTY_DATA);
  const [sourceLabel, setSourceLabel] = useState("");
  const [mapping, setMapping] = useState({});

  const acceptParsed = useCallback((parsed, label, suggestedMapping) => {
    const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
    const columns = Array.isArray(parsed?.columns) ? parsed.columns : [];

    if (!columns.length || !rows.length) {
      throw new Error("Add a header row and at least one pipeline row.");
    }

    if (!suggestPipelineMapping(columns).stage_name && columns.some((column) => /^(full name|phone number|email|company name)$/i.test(column.trim()))) {
      throw new Error("This looks like a lead spreadsheet, not a workflow. Use Lead table → From a lead spreadsheet to design its columns, or Leads → Import to add people.");
    }

    setData({
      columns,
      rows,
      totalRows: parsed.totalRows ?? parsed.total_rows ?? rows.length,
      truncated: Boolean(parsed.truncated),
    });
    setSourceLabel(label);
    setMapping(
      suggestedMapping && Object.keys(suggestedMapping).length
        ? suggestedMapping
        : suggestPipelineMapping(columns)
    );
    setError("");
  }, []);

  const loadFile = useCallback(async (file) => {
    setLoading(true);
    setError("");
    try {
      acceptParsed(await parsePipelineFile(file), file.name);
    } catch (loadError) {
      setError(readError(loadError, "We couldn't read that file."));
    } finally {
      setLoading(false);
    }
  }, [acceptParsed]);

  const loadPaste = useCallback((text) => {
    setError("");
    try {
      acceptParsed(parsePastedPipelineRows(text), "Pasted spreadsheet rows");
    } catch (loadError) {
      setError(readError(loadError, "We couldn't read the pasted rows."));
    }
  }, [acceptParsed]);

  const loadGoogleSheet = useCallback(async (url) => {
    setLoading(true);
    setError("");
    try {
      const response = await loadPipelineGoogleSheet(url.trim());
      const payload = response?.data?.data || {};
      acceptParsed(payload, "Public Google Sheet", payload.suggested_mapping);
    } catch (loadError) {
      setError(readError(loadError, "We couldn't load that Google Sheet."));
    } finally {
      setLoading(false);
    }
  }, [acceptParsed]);

  const setFieldMapping = useCallback((field, column) => {
    setMapping((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        if (next[key] === column) delete next[key];
      });
      if (column) next[field] = column;
      else delete next[field];
      return next;
    });
  }, []);

  const runImport = useCallback(async (pipeline) => {
    if (!pipelineId && !pipeline.name.trim()) {
      setError("Pipeline name is required.");
      return null;
    }
    if (!mapping.stage_name) {
      setError("Map one column to Stage name before importing.");
      return null;
    }

    setImporting(true);
    setError("");
    try {
      if (!pipelineId && pipeline.table_schema) validateBlueprint(pipeline.table_schema);
      const response = pipelineId
        ? await updatePipelineRows(pipelineId, { mapping, rows: data.rows })
        : await importPipelineRows({
          name: pipeline.name.trim(),
          description: pipeline.description.trim() || null,
          is_default: pipeline.is_default,
          ...(pipeline.table_schema ? { table_schema: pipeline.table_schema } : {}),
          mapping,
          rows: data.rows,
        });
      return response?.data?.data || null;
    } catch (importError) {
      setError(readError(importError, "The pipeline import could not be completed."));
      return null;
    } finally {
      setImporting(false);
    }
  }, [data.rows, mapping, pipelineId]);

  const resetSource = useCallback(() => {
    setData(EMPTY_DATA);
    setSourceLabel("");
    setMapping({});
    setError("");
  }, []);

  return {
    loading,
    importing,
    error,
    columns: data.columns,
    rows: data.rows,
    totalRows: data.totalRows,
    truncated: data.truncated,
    sourceLabel,
    mapping,
    loadFile,
    loadPaste,
    loadGoogleSheet,
    setFieldMapping,
    runImport,
    resetSource,
  };
}
